import type { ManiaLane } from '@shared/maniaNote';
import { PianoFxSystem } from '../piano/render/pianoFx';
import { HIT_STOP_MAX_MS, SCORE_HUD_TARGET_TOP_PAD_PX } from './render/guitarRenderConstants';
import { computeLayout } from './guitarPlayfieldGeometry';
import { GuitarSceneRenderer, getLaneStrumCentre } from './render/guitarDrawScene';
import { GuitarSceneAtmosphere } from './render/guitarSceneAtmosphere';
import { GuitarNoteRenderer } from './render/guitarDrawNotes';
import { GuitarHudRenderer } from './render/guitarDrawHud';
import {
  drawGuitarCountdown,
  drawGuitarFloatingTexts,
  drawGuitarPausedOverlay,
  drawGuitarScorePops,
  GuitarLeadInBannerState,
} from './render/guitarDrawOverlays';
import { GuitarFxBoost } from './render/guitarFxBoost';
import type { GuitarRenderInput } from './render/guitarRenderTypes';
import { getRenderQuality, type RenderQuality } from '../rendering/renderQuality';

/**
 * 4-lane Guitar fretboard renderer. Mirrors the {@link
 * import('../piano/pianoRenderer.js').PianoRenderer} contract — a
 * thin orchestrator over scene / notes / hud / overlay subsystems
 * — so the Guitar session can call the same lifecycle hooks
 * (`requestHitStop`, `clearLoopBoundFx`, `getLaneHitPoint`,
 * `getScoreHudTarget`) the Piano session uses, while owning a
 * different visual model (perspective trapezoid + GH-classic gem
 * colours + sustain ribbons in lieu of Piano's flat lanes + tile
 * stack).
 *
 * Reuses {@link PianoFxSystem} verbatim because particle bursts,
 * shockwaves, and lane flashes are mode-agnostic — the FX system
 * only cares about (x, y) positions and lane indices, both of which
 * the Guitar session forwards through the same spec types Piano
 * does.
 */
export class GuitarRenderer {
  private ctx: CanvasRenderingContext2D;
  private canvas: HTMLCanvasElement;
  private lastDpr = 1;
  /** Remaining hit-stop time (seconds). While >0, animation dt is frozen. */
  private hitStopRemaining = 0;
  private reducedMotion = false;
  private quality: RenderQuality;
  private lastFrameAt = performance.now();
  private fx: PianoFxSystem;
  private fxBoost: GuitarFxBoost;
  private hud = new GuitarHudRenderer();
  private leadInBanner = new GuitarLeadInBannerState();
  private notes = new GuitarNoteRenderer();
  private scene = new GuitarSceneRenderer();
  private atmosphere = new GuitarSceneAtmosphere();

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('GuitarRenderer: 2D context unavailable');
    }
    this.ctx = ctx;
    if (typeof window !== 'undefined' && window.matchMedia) {
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
    this.quality = getRenderQuality();
    this.fx = new PianoFxSystem(this.reducedMotion, this.quality);
    this.fxBoost = new GuitarFxBoost(this.reducedMotion, this.quality);
  }

  /**
   * Trigger a brief render-time freeze. Particle / shockwave /
   * flash / shake animations hold their current frame for `ms`
   * milliseconds; the engine and audio are NOT affected. Mirrors
   * {@link import('../piano/pianoRenderer').PianoRenderer.requestHitStop}.
   */
  requestHitStop(ms: number): void {
    if (this.reducedMotion) {
      return;
    }
    const clamped = Math.min(ms, HIT_STOP_MAX_MS);
    this.hitStopRemaining = Math.max(this.hitStopRemaining, clamped / 1000);
  }

  /**
   * Drop renderer-owned FX state on loop boundaries. The session
   * calls this between loops so a residual particle / shockwave
   * from loop N doesn't ghost-render through loop N+1's opening
   * countdown.
   */
  clearLoopBoundFx(): void {
    this.fx.clear();
    this.fxBoost.clear();
    this.atmosphere.reset();
    this.hitStopRemaining = 0;
    this.hud.reset();
    this.leadInBanner.reset();
    this.notes.reset();
    this.lastFrameAt = performance.now();
  }

  draw(input: GuitarRenderInput): void {
    const { width, height, chart, paused } = input;
    if (width <= 0 || height <= 0) {
      return;
    }
    this.ensureBackingStore(width, height);
    const ctx = this.ctx;
    const palette = chart.palette;
    const layout = computeLayout(width, height);

    const now = performance.now();
    const wallDt = Math.min(0.05, Math.max(0, (now - this.lastFrameAt) / 1000));
    this.lastFrameAt = now;
    let animDt: number;
    if (this.hitStopRemaining > 0) {
      this.hitStopRemaining = Math.max(0, this.hitStopRemaining - wallDt);
      animDt = 0;
    } else {
      animDt = wallDt;
    }

    this.fx.consumeEmissions(input.particleEmissions);
    this.fx.consumeShockwaveSpecs(input.shockwaveSpecs);
    this.fx.consumeLaneFlashSpecs(input.laneFlashSpecs);
    this.fx.advanceAll(animDt);
    this.fxBoost.update(
      this.fx.getLaneFlashes(),
      width,
      height,
      input.state.combo,
      animDt,
    );
    this.atmosphere.advance(animDt);
    this.notes.advance(animDt);
    this.hud.advance(input.state.combo, animDt);
    this.leadInBanner.advance(input.countdown, animDt);

    const shakeX = this.reducedMotion ? 0 : (Math.random() * 2 - 1) * input.shake.amount;
    const shakeY = this.reducedMotion ? 0 : (Math.random() * 2 - 1) * input.shake.amount;

    ctx.save();
    ctx.translate(shakeX, shakeY);

    this.scene.drawBackground(
      ctx,
      width,
      height,
      palette,
      input.state.combo,
      this.reducedMotion,
      this.quality,
    );
    this.atmosphere.drawHorizonGlow(
      ctx,
      layout,
      input.state.combo,
      this.reducedMotion,
      this.quality,
    );
    this.scene.drawHighway(ctx, layout, palette, input.pressedLanes);
    this.atmosphere.drawSpeedLines(ctx, layout, this.reducedMotion, this.quality);
    this.atmosphere.drawPressChevrons(
      ctx,
      layout,
      input.pressedLanes,
      this.reducedMotion,
      this.quality,
    );
    this.scene.drawLaneFlashes(ctx, this.fx.getLaneFlashes(), layout);
    this.notes.draw(ctx, input, layout, this.reducedMotion, this.quality);
    this.scene.drawStrumBar(
      ctx,
      layout,
      palette,
      input.state.combo,
      input.beat,
      input.pressedLanes,
      input.keyLabels,
      this.reducedMotion,
      this.quality,
    );
    this.fx.drawShockwaves(ctx);
    this.fx.drawParticles(ctx);
    this.fxBoost.drawLightning(ctx, layout);
    drawGuitarFloatingTexts(
      ctx,
      input,
      layout,
      width,
      height,
      this.fx,
      this.leadInBanner,
      this.reducedMotion,
      this.quality,
    );
    drawGuitarScorePops(ctx, input.scorePops, this.reducedMotion, this.quality);
    this.hud.draw(ctx, input, layout, this.reducedMotion, this.quality);

    if (this.leadInBanner.shouldDrawCountdown(input.countdown)) {
      drawGuitarCountdown(
        ctx,
        input.countdown,
        width,
        height,
        palette,
        this.leadInBanner.getGoPulseRemaining(),
        this.leadInBanner.getCountdownRings(),
        this.reducedMotion,
        this.quality,
      );
    }
    this.atmosphere.drawVignette(ctx, width, height, this.quality);
    if (paused) {
      drawGuitarPausedOverlay(ctx, width, height, palette);
    }

    ctx.restore();
  }

  /**
   * Resize the canvas backing store to match the current viewport at
   * the device-pixel-ratio so HiDPI screens stay sharp. Only re-
   * allocates when DPR or CSS dimensions actually changed.
   */
  private ensureBackingStore(width: number, height: number): void {
    const rawDpr = window.devicePixelRatio || 1;
    const dpr = Math.min(rawDpr, this.quality.dprCap);
    const targetW = Math.round(width * dpr);
    const targetH = Math.round(height * dpr);
    if (
      this.canvas.width !== targetW ||
      this.canvas.height !== targetH ||
      this.lastDpr !== dpr
    ) {
      this.canvas.width = targetW;
      this.canvas.height = targetH;
      this.lastDpr = dpr;
    }
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  /**
   * Public accessor used by the session to position score-pop
   * flyers at the HUD score readout's approximate location, so pops
   * always arc toward where the score lives. Anchored to the
   * playing field's right edge at the HUD top — same convention
   * Piano uses.
   */
  getScoreHudTarget(canvasWidth: number, canvasHeight: number): { x: number; y: number } {
    const layout = computeLayout(canvasWidth, canvasHeight);
    return {
      x: layout.centreX + layout.bottomStackWidth / 2,
      y: SCORE_HUD_TARGET_TOP_PAD_PX,
    };
  }

  /**
   * Centre point of the named lane at the strum bar in canvas CSS
   * px. Session-side FX (particle bursts, shockwaves, score-pops)
   * spawn from this point so the eye is anchored to the lane that
   * just resolved a gem. Renderer-owned to keep lane geometry
   * single-source.
   */
  getLaneHitPoint(
    lane: ManiaLane,
    canvasWidth: number,
    canvasHeight: number,
  ): { x: number; y: number } {
    return getLaneStrumCentre(canvasWidth, canvasHeight, lane);
  }
}
